import { PageFn } from '@client/Router';
import {
  withAuthenticationRequired,
  WithAuthenticationRequiredOptions,
} from '@auth0/auth0-react';
import { isNullOrUndefined, SessionType } from '@shared';

import { useClientData } from '../hooks/useClientData';
import { FullScreenLoadingOverlay } from '@client/components';
import { useAuth0 } from '@auth0/auth0-react';
import { useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchSubordinateAdvisorsByAdvisorIdRequest,
  selectAdvisorHierarchyState,
  selectClientErrorMessage,
  selectSession,
  UnauthorizedPage,
} from '..';
import { useEffect, useMemo } from 'react';
import { Navigate } from 'react-router-dom';

export const ClientRoute = ({
  component,
  allowedSessionTypes,
  ...args
}: {
  component: PageFn;
  allowedSessionTypes: SessionType[];
} & WithAuthenticationRequiredOptions) => {
  const params = useParams();
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const { client } = useClientData();
  const clientError = useSelector(selectClientErrorMessage)
  const argsJson = JSON.stringify(args);
  const ProtectedComponent = useMemo(
    () => withAuthenticationRequired(component, args),
    [component, argsJson]
  );
  const session = useSelector(selectSession);
  const { subordinateAdvisors } = useSelector(selectAdvisorHierarchyState);
  const dispatch = useDispatch();

  useEffect(() => {
    if (
      session.sessionType === SessionType.advisor &&
      isNullOrUndefined(subordinateAdvisors) &&
      session.advisor?.advisorId
    ) {
      // fetch subordinate advisors if not already fetched. This is mainly for PDF report download
      // since it opens in a new window and fetch session does not fetch subordinate advisors
      dispatch(
        fetchSubordinateAdvisorsByAdvisorIdRequest({
          advisorId: session.advisor.advisorId,
        }),
      );
    }
  }, [session.sessionType, subordinateAdvisors, session.advisor?.advisorId]);

  if (!isAuthenticated) {
    loginWithRedirect();
  }

  if (clientError?.includes('Requested client not found.')) {
    return <Navigate to="/client/home" replace />;
  }

  if (shouldShowLoading(client, session, subordinateAdvisors)) {
    return <FullScreenLoadingOverlay isLoading={true} />;
  }

  if (session.sessionType === SessionType.advisor) {
    const { advisor } = session;
    if (
      !advisorUserHasAccessToViewClient(
        advisor!,
        subordinateAdvisors || [],
        client!,
      )
    ) {
      return <UnauthorizedPage />;
    }
  }

  if (session.sessionType === SessionType.client) {
    if (
      !clientUserHasAccessToViewClient(
        client!.clientId,
        session.clientProfiles!,
      )
    ) {
      return <UnauthorizedPage />;
    }
  }

  return <ProtectedComponent {...params} />;
};

function shouldShowLoading(
  client: Client | null,
  session: Session,
  subordinateAdvisors: Advisor[] | null,
): boolean {
  if (!client) {
    return true;
  }

  if (session.sessionType === SessionType.advisor) {
    return !session.advisor || isNullOrUndefined(subordinateAdvisors);
  }

  if (session.sessionType === SessionType.client) {
    return !session.clientProfiles;
  }

  return false;
}

function advisorUserHasAccessToViewClient(
  advisor: Advisor,
  subordinateAdvisors: Advisor[],
  client: Client,
): boolean {
  if (client.advisorId === advisor.advisorId) {
    return true;
  }
  const hasSubordinateAdvisors = subordinateAdvisors?.length > 0;
  return (
    hasSubordinateAdvisors &&
    subordinateAdvisors.some(
      subordinate => subordinate.advisorId === client.advisorId,
    )
  );
}

function clientUserHasAccessToViewClient(
  clientId: string,
  clientProfiles: ClientProfile[],
): boolean {
  // if client user, check if clientId is within the clientProfiles
  return clientProfiles.some(profile => profile.clientId === clientId);
}
